import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { ComponentContext } from '../../../../ContextWrappers/NRComponentContextWrapper';
import useNRMutation from '../../../../graphql/UseNRMutation';
import { GET_VULNERABILITY_JIRA_TICKETS, QL_JIRA_INTEGRATION_CREATE_ISSUE } from '../../../../graphql/queries/jiraIntegration';
import { FiltersContext } from '../../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import {
  CREATE_JIRA_TICKET_LABELS,
  JIRA_UNASSIGNED_ASSIGNEE_ID,
  isJiraUnassignedAssignee
} from './createJiraTicket.constants';
import {
  buildCreateJiraIssueFields,
  buildCreateJiraTicketDescription,
  buildCreateJiraTicketSummary,
  getUnsupportedRequiredJiraFields,
  isJiraIssueFieldsReady
} from './createJiraTicket.utils';
import { NRCreateVulnerabilityJiraTicketActionsController } from './NRCreateVulnerabilityJiraTicketActions';
import { useJiraCreateTicketOptionLists } from './useJiraCreateTicketOptionLists';
import { useJiraCreateTicketSchema } from './useJiraCreateTicketSchema';

export function useCreateVulnerabilityJiraTicket({ vulnerabilityData = {}, assetId, advisoryId }) {
  const { enqueueSnackbar } = useSnackbar();
  const { asset } = useContext(ComponentContext);
  const { drawerContent, setDrawerContent, drawerTitle, setDrawerTitle, setHasBack, setDrawerFooterExtraComponents } =
    useContext(FiltersContext);

  const pendingCreateRef = useRef(false);
  const dynamicFormRef = useRef(null);
  const footerActionsBridgeRef = useRef({
    canCreate: false,
    isCreating: false,
    onCancel: () => {},
    onCreate: () => {},
    subscribe: null
  });

  const [spaceId, setSpaceId] = useState('');
  const [issueTypeId, setIssueTypeId] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [assigneeId, setAssigneeId] = useState(JIRA_UNASSIGNED_ASSIGNEE_ID);
  const [dynamicFieldsValid, setDynamicFieldsValid] = useState(true);

  const {
    spaceOptions,
    issueTypeOptions,
    issueTypesLoading,
    issueFields,
    issueFieldsLoading,
    issueFieldsCalled,
    priorityField,
    assigneeField,
    requiredRpcOptionSources,
    showPriorityField,
    priorityOptions,
    refreshIssueFields
  } = useJiraCreateTicketSchema({ spaceId, issueTypeId });

  const { projectUsers, rpcOptions } = useJiraCreateTicketOptionLists({
    spaceId,
    issueTypeId,
    issueFields,
    requiredRpcOptionSources
  });

  const ticketArgs = useMemo(
    () => ({
      assetId,
      advisoryId,
      componentId: vulnerabilityData?.componentId || null
    }),
    [advisoryId, assetId, vulnerabilityData?.componentId]
  );

  const [createIssue, createResult, isCreating, createError] = useNRMutation(
    QL_JIRA_INTEGRATION_CREATE_ISSUE,
    'jiraIntegrationCreateIssue',
    undefined,
    CREATE_JIRA_TICKET_LABELS.CREATE_ERROR,
    {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_VULNERABILITY_JIRA_TICKETS, variables: { args: ticketArgs } }]
    }
  );

  useEffect(() => {
    if (!showPriorityField) {
      setPriority('');
      return;
    }

    if (priorityOptions.length === 1) {
      setPriority(priorityOptions[0].id);
      return;
    }

    setPriority(current => (priorityOptions.some(option => option.id === current) ? current : ''));
  }, [priorityOptions, showPriorityField]);

  useEffect(() => {
    if (spaceOptions.length === 1 && !spaceId) {
      setSpaceId(spaceOptions[0].value);
    }
  }, [spaceId, spaceOptions]);

  useEffect(() => {
    setSummary(
      buildCreateJiraTicketSummary({
        cve: vulnerabilityData.cve,
        name: vulnerabilityData.name,
        version: vulnerabilityData.version,
        severity: vulnerabilityData.severity
      })
    );
    setDescription(
      buildCreateJiraTicketDescription({
        severity: vulnerabilityData.severity,
        cvssScore: vulnerabilityData.cvssScore,
        epssScore: vulnerabilityData.epssScore,
        fixVersions: vulnerabilityData.fixVersions,
        vexStatus: vulnerabilityData.currentRemediation?.status,
        isReachable: vulnerabilityData.isReachable,
        name: vulnerabilityData.name,
        version: vulnerabilityData.version,
        assetName: asset?.name,
        assetId
      })
    );
  }, [
    asset?.name,
    assetId,
    vulnerabilityData.cve,
    vulnerabilityData.cvssScore,
    vulnerabilityData.currentRemediation?.status,
    vulnerabilityData.epssScore,
    vulnerabilityData.fixVersions,
    vulnerabilityData.isReachable,
    vulnerabilityData.name,
    vulnerabilityData.severity,
    vulnerabilityData.version
  ]);

  const hasSpace = Boolean(spaceId);
  const hasIssueType = Boolean(issueTypeId);
  const fieldsAfterSpaceEnabled = hasSpace;
  const fieldsAfterIssueTypeEnabled = hasSpace && hasIssueType;
  const issueFieldsReady = isJiraIssueFieldsReady({
    hasIssueType,
    issueFieldsCalled,
    issueFieldsLoading
  });
  const prioritySatisfied = !showPriorityField || !priorityField?.required || Boolean(priority);
  const assigneeSatisfied = !assigneeField?.required || !isJiraUnassignedAssignee(assigneeId);
  const unsupportedRequiredFields = useMemo(
    () => getUnsupportedRequiredJiraFields(issueFields),
    [issueFields]
  );
  const canCreate =
    hasSpace &&
    hasIssueType &&
    issueFieldsReady &&
    Boolean(summary.trim()) &&
    Boolean(assetId) &&
    Boolean(advisoryId) &&
    Boolean(vulnerabilityData.componentId) &&
    prioritySatisfied &&
    assigneeSatisfied &&
    dynamicFieldsValid &&
    unsupportedRequiredFields.length === 0 &&
    !isCreating;

  useEffect(() => {
    if (!hasIssueType || issueFieldsLoading) {
      setDynamicFieldsValid(true);
    }
  }, [hasIssueType, issueFieldsLoading]);

  const handleCancel = useCallback(() => {
    setDrawerFooterExtraComponents(null);
    setHasBack(drawerContent.length - 1 > 1);
    setDrawerTitle(drawerTitle.slice(0, -1));
    setDrawerContent(drawerContent.slice(0, -1));
  }, [
    drawerContent,
    drawerTitle,
    setDrawerContent,
    setDrawerTitle,
    setDrawerFooterExtraComponents,
    setHasBack
  ]);

  useEffect(() => {
    if (!pendingCreateRef.current || !createResult) {
      return;
    }

    pendingCreateRef.current = false;

    if (createResult.success) {
      enqueueSnackbar(CREATE_JIRA_TICKET_LABELS.CREATE_SUCCESS, { variant: 'success' });
      handleCancel();
      return;
    }

    if (createResult.success === false) {
      enqueueSnackbar(createResult.error || CREATE_JIRA_TICKET_LABELS.CREATE_ERROR, { variant: 'error' });
    }
  }, [createResult, enqueueSnackbar, handleCancel]);

  useEffect(() => {
    if (createError && pendingCreateRef.current) {
      pendingCreateRef.current = false;
    }
  }, [createError]);

  const handleSpaceChange = useCallback(nextSpaceId => {
    setSpaceId(nextSpaceId);
    setIssueTypeId('');
    setPriority('');
    setAssigneeId(JIRA_UNASSIGNED_ASSIGNEE_ID);
  }, []);

  const handleIssueTypeChange = useCallback(nextIssueTypeId => {
    setIssueTypeId(nextIssueTypeId);
    setPriority('');
    setAssigneeId(JIRA_UNASSIGNED_ASSIGNEE_ID);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!canCreate) {
      return;
    }

    const dynamicFields = dynamicFormRef.current?.getFormattedFields
      ? await dynamicFormRef.current.getFormattedFields()
      : [];

    if (dynamicFields === null) {
      return;
    }

    pendingCreateRef.current = true;

    createIssue({
      variables: {
        args: {
          spaceId,
          issueTypeId,
          summary: summary.trim(),
          description,
          fields: [
            ...buildCreateJiraIssueFields({
              assigneeId,
              priorityId: priority,
              issueFields
            }),
            ...dynamicFields
          ],
          vulnerability: {
            assetId,
            vulnerabilityId: advisoryId,
            componentId: vulnerabilityData.componentId
          }
        }
      }
    });
  }, [
    advisoryId,
    assetId,
    assigneeId,
    canCreate,
    createIssue,
    description,
    issueFields,
    issueTypeId,
    priority,
    spaceId,
    summary,
    vulnerabilityData.componentId
  ]);

  footerActionsBridgeRef.current.onCancel = handleCancel;
  footerActionsBridgeRef.current.onCreate = handleCreate;

  useEffect(() => {
    setDrawerFooterExtraComponents(
      <NRCreateVulnerabilityJiraTicketActionsController bridgeRef={footerActionsBridgeRef} />
    );

    return () => {
      setDrawerFooterExtraComponents(null);
    };
  }, [setDrawerFooterExtraComponents]);

  useEffect(() => {
    footerActionsBridgeRef.current.canCreate = canCreate;
    footerActionsBridgeRef.current.isCreating = isCreating;
    footerActionsBridgeRef.current.subscribe?.();
  }, [canCreate, isCreating]);

  return {
    dynamicFormRef,
    spaceId,
    issueTypeId,
    summary,
    description,
    priority,
    assigneeId,
    spaceOptions,
    issueTypeOptions,
    priorityOptions,
    projectUsers,
    showPriorityField,
    priorityRequired: Boolean(priorityField?.required),
    assigneeRequired: Boolean(assigneeField?.required),
    fieldsAfterSpaceEnabled,
    fieldsAfterIssueTypeEnabled,
    isIssueTypesLoading: Boolean(spaceId) && issueTypesLoading,
    isIssueFieldsLoading: Boolean(issueTypeId) && issueFieldsLoading,
    issueFields,
    rpcOptions,
    setSummary,
    setDescription,
    setPriority,
    setAssigneeId,
    setDynamicFieldsValid,
    handleSpaceChange,
    handleIssueTypeChange,
    handleRefreshIssueFields: refreshIssueFields
  };
}
